"use client";

import { useState } from "react";
import { PieChart, Pie, ResponsiveContainer } from "recharts";
import { useAnimatedNumber } from "@/hooks/useAnimatedNumber";
import SegmentedToggle from "./SegmentedToggle";

// ─── Statische Demo-Daten für die Landingpage ──────────────────────────────
// Kein Auth-/API-Bezug bewusst: diese Preview soll unabhängig vom
// Anmeldestatus immer gleich aussehen. Echte Werte gibt es erst im Tool
// selbst (siehe components/tool/Dashboard.tsx). Der Jahr/Monat-Umschalter
// ist aber eine echte, im Tool selbst vorhandene Funktion (PeriodenSelect).

const CO2_JAHR = 387; // t
const KOSTEN_JAHR = 342180; // €

const MODAL_SPLIT = [
  { label: "Auto", value: 49, color: "#1e3a5f" },
  { label: "ÖPNV", value: 14, color: "#2563eb" },
  { label: "Fahrrad", value: 14, color: "#f59e0b" },
  { label: "Zu Fuß", value: 22, color: "#16a34a" },
  { label: "Sonstige", value: 1, color: "#94a3b8" },
];

const SCORES = ["A", "B", "C", "D", "E"];
const SCORE_COLORS: Record<string, string> = {
  A: "bg-green-500",
  B: "bg-lime-400",
  C: "bg-yellow-400",
  D: "bg-orange-400",
  E: "bg-red-500",
};

type Periode = "jahr" | "monat";

function formatNumber(n: number): string {
  const rounded = Math.round(n);
  if (rounded >= 1_000_000) return (rounded / 1_000_000).toFixed(1).replace(".", ",") + " Mio";
  return rounded.toLocaleString("de-DE");
}

function MiniCard({
  label,
  children,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`bg-white rounded-lg border border-gray-200 shadow-sm p-2.5 sm:p-3 min-w-0 ${className}`}>
      <p className="text-[10px] sm:text-xs font-semibold text-gray-500 mb-1.5 sm:mb-2 truncate">{label}</p>
      {children}
    </div>
  );
}

export default function DashboardPreview() {
  const [periode, setPeriode] = useState<Periode>("jahr");
  const faktor = periode === "jahr" ? 1 : 1 / 12;

  const co2 = useAnimatedNumber(CO2_JAHR * faktor);
  const kosten = useAnimatedNumber(KOSTEN_JAHR * faktor);

  return (
    <div className="w-full">
      <div className="flex justify-end mb-2 sm:mb-3">
        <SegmentedToggle
          value={periode}
          onChange={setPeriode}
          options={[
            { value: "jahr", label: "Pro Jahr" },
            { value: "monat", label: "Pro Monat" },
          ]}
        />
      </div>

      <div className="grid grid-cols-4 gap-2 sm:gap-3">
        <MiniCard label="MobiScore">
          <div className="flex gap-1">
            {SCORES.map((s) => (
              <div
                key={s}
                className={`w-5 h-5 sm:w-6 sm:h-6 rounded flex items-center justify-center text-[10px] sm:text-xs font-bold text-white ${SCORE_COLORS[s]}
                  ${s === "C" ? "ring-2 ring-offset-1 ring-gray-800 scale-110" : "opacity-70"}`}
              >
                {s}
              </div>
            ))}
          </div>
        </MiniCard>

        <MiniCard label="CO₂">
          <p className="text-base sm:text-lg font-bold text-blue-600 tabular-nums whitespace-nowrap">{formatNumber(co2)} t</p>
          <p className="text-xs text-gray-400">{periode === "jahr" ? "pro Jahr" : "pro Monat"}</p>
        </MiniCard>

        <MiniCard label="Kosten">
          <p className="text-base sm:text-lg font-bold text-blue-600 tabular-nums whitespace-nowrap">{formatNumber(kosten)} €</p>
          <p className="text-xs text-gray-400">{periode === "jahr" ? "pro Jahr" : "pro Monat"}</p>
        </MiniCard>

        <MiniCard label="Modal Split">
          <div className="flex h-12 w-12 items-center justify-center 2xl:h-20 2xl:w-20">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={MODAL_SPLIT.map((i) => ({ ...i, fill: i.color }))}
                  dataKey="value"
                  nameKey="label"
                  innerRadius="42%"
                  outerRadius="72%"
                  paddingAngle={2}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </MiniCard>
      </div>
    </div>
  );
}
